import { useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  updateCloudMaterial,
  updateTemporaryMaterial,
  fetchMaterialById,
} from '../../features/materials/api';
import { resolveMaterialUrls, buildDragData } from '../../features/materials/materialUrl';
import { rasterErrorMessage, useEnsureRasterAsset } from '../../features/materials/ensureRasterAsset';
import { useGcsSignedUrlMap } from '../../features/materials/useMaterialUrls';
import {
  useTemporaryMaterials,
  useCloudMaterials,
  useMaterialWorkflowStages,
  useTemporaryMaterialsPaged,
  useCloudMaterialsPaged,
  useMaterialsPagedMeta,
  PAGE_LIMIT,
} from '../../features/materials/hooks';
import { buildSourceFilterGroups, labelMaterialSource, SOURCE_TYPE_LABELS } from '../../features/materials/sourceLabels';
import type { CloudMaterial, TemporaryMaterial } from '../../features/materials/materialsSlice';
import { MaterialCard } from '../materials/shared/MaterialCard';
import { calculateAspectRatio, createInfoBadges, createRasterStatusBadges, createSvgBadgeLabels, isSvgContentType, normalizeFileType, getVideoResolutionLabel, formatDurationBadge, type InfoBadge } from '../materials/shared/materialUtils';
import type { GenerationAsset } from '../generation/state/types';
import { useMaterialsPanelStore, type MaterialsPanelMode } from '../../stores/materialsPanelStore';
import { CloudMaterialsModal } from '../materials/CloudMaterialsModal';
import { MaterialPreviewModal } from '../materials/shared/MaterialPreviewModal';
import { useToast } from '../../hooks/useToast';
import { useTags } from '../../features/tags';
import { isBrandCutMaterial, isSoundLogoMaterial } from '../../features/tags/constants';
import { useIsMobile } from '../../hooks/useIsMobile';
import { LoadMoreButton } from '../common/LoadMoreButton';
import { AudioTrackTable } from '../common/AudioTrackTable';
import { fetchImageSpecById, checkMaterialMatchesSpec } from '../../features/media/api';
import type { ImageAssetSpec } from '../../features/media/api';
import {
  makeCompositeKey,
  normalizeAsinForDisplay,
  type DisplayEntry,
  type MaterialsTab,
  type PlaceholderEntry,
  type TabType,
  type ControlledTabType,
} from './simpleMaterialsList/types';
import { useMaterialsFilters } from './simpleMaterialsList/useMaterialsFilters';
import { useFilterOptions } from './simpleMaterialsList/useFilterOptions';
import { useBulkMaterialActions, type BulkEditMode } from './simpleMaterialsList/useBulkMaterialActions';
import { MaterialsFilterBar } from './simpleMaterialsList/MaterialsFilterBar';
import { MaterialsPagination } from './simpleMaterialsList/MaterialsPagination';
import { brandConfig } from '../../config/brands';
import { MaterialsBulkActions } from './simpleMaterialsList/MaterialsBulkActions';
import { StoryboardTabPane } from './simpleMaterialsList/StoryboardTabPane';
import { pickNextMaterialAfterDelete } from './simpleMaterialsList/pickNextMaterialAfterDelete';
import './SimpleMaterialsList.css';

// 後方互換の再エクスポート (RightPanelWrapper / MaterialsBrowsePage が参照)
export { MATERIALS_FILTER_CARRYOVER_KEY, type MaterialsFilterSnapshot } from './simpleMaterialsList/types';
import { type MaterialsFilterSnapshot } from './simpleMaterialsList/types';

interface Props {
  mode?: MaterialsPanelMode;
  selectedAsin?: string;
  selectedBrandId?: string;
  onSelectMaterial?: (material: CloudMaterial | TemporaryMaterial) => void;
  bulkEditMode?: BulkEditMode;
  onBulkEditComplete?: () => void;
  // Optional generation state props (for manage mode with GenerationProvider)
  selectedAssets?: any[];
  onAddAsset?: (asset: any) => void;
  onRemoveAsset?: (id: string) => void;
  // Tab control
  activeTab?: ControlledTabType;
  onTabChange?: (tab: ControlledTabType) => void;
  // Amazon image type for filtering (main or sub image)
  imageType?: 'main' | 'sub';
  // 一括操作バーの portal 先。指定された場合はその要素の末端 (absolute) に表示する。
  // 未指定の場合は document.body にビューポート fixed で表示する。
  bulkActionsTarget?: HTMLElement | null;
  // モーダル等で素材ピッカーとして埋め込むときの調整用。
  // 種別/取得元フィルタを隠し、種別やアスペクト比の初期フィルタを与える。
  hideFileTypeFilter?: boolean;
  hideSourceTypeFilter?: boolean;
  /** アスペクト比フィルタを隠す (音声ピッカー用) */
  hideAspectRatioFilter?: boolean;
  /** カードのファイル容量バッジ (n.nMB) を出さない (BGM ピッカーなど容量が判断材料にならないとき) */
  hideFileSizeBadge?: boolean;
  // ストーリーボードプレビュー用の仮想タブを出さない (素材ピッカーとして埋め込むとき用)
  hideStoryboardTab?: boolean;
  // Amazon専用の「メイン/サブ画像の推奨規定に合致」フィルタを隠し、常にOffにする。
  // (動画ピッカー等の非AmazonコンテキストでこのAmazon専用機能が混入しないようにする)
  hideAmazonComplianceFilter?: boolean;
  initialFileTypes?: string[];
  initialAspectRatios?: string[];
  /** 取得元フィルタの初期値 (モーダル埋め込み時) */
  initialSourceTypes?: string[];
  // 最大尺(秒)フィルタを 3 点リーダー内ではなく常に表に出す。
  // ストーリーボード / ブランドカットからの呼び出しでは短尺動画を選びたい需要が
  // 強いため、デフォルト表示にする。それ以外 (素材ページ等) では非推奨フィルタとして
  // 3 点リーダー内に隠す。
  showMaxDurationFilter?: boolean;
  // 検索ボックスを最初から展開し、開閉シェブロンを隠す (ピッカー用)。
  alwaysShowSearch?: boolean;
  // ブランドカットタグ付き素材を最上段に優先表示する。
  // タイトル/エンドカードの素材ピッカー (MaterialsPickerModal) からの呼び出し専用。
  // 素材ページ本体の並び順には影響させないため、デフォルトは false。
  brandCutFirst?: boolean;
  // サウンドロゴタグ付き素材を最上段に優先表示する。
  // シーンのサウンドロゴ選択ピッカー (MaterialsPickerModal, 音声モード) からの呼び出し専用。
  soundLogoFirst?: boolean;
  // 一覧の優先順位 (大きいほど上・同点はサーバー順)。ピッカー専用 (非ページング時のみ有効)。
  prioritize?: (material: CloudMaterial | TemporaryMaterial) => number;
  // 一覧から除外する素材ID。既に紐付け済みの素材をピッカーから隠す用途。
  excludeMaterialIds?: string[];
  // 動画素材カードにインライン再生ボタンを出す。
  // 動画ピッカー (MaterialsPickerModal) 専用。素材ページ本体には出さない。
  playableVideoCards?: boolean;
  // カードの「詳細」ボタンを隠す (ピッカー用)。
  hideDetailsButton?: boolean;
  // ピッカー用: タイル全体のクリックで onSelectMaterial が発火し、ホバーで枠線を出す (以前は常時表示の「選択」ボタン)。
  showSelectButton?: boolean;
  // ページ番号ジャンプ式のページネーションを有効にする (素材ブラウズページ専用)。
  // true のとき: 内部スクロール/無限スクロールをやめ、現在ページのみ offset 取得し、
  // グリッド下にページ番号 UI を出す。右パネル/ピッカーは false のまま (カーソル無限スクロール)。
  paginated?: boolean;
  // 音声ピッカー (MaterialsPickerModal, 音声のみ・select モード) 専用: カードのグリッドの代わりに
  // 音声選択モーダル共通の一覧テーブル (AudioTrackTable) で出す。行のクリックで選ぶ。
  // 'auto' = 表示中の素材がすべて音声のときだけテーブル、動画が混ざればカード (効果音ピッカーで
  // 動画素材も選べるようにしたとき。動画はサムネイルで見分けたいのでカードに戻す)
  audioTableLayout?: boolean | 'auto';
  // 音声ピッカー専用: 共有 / 個人フォルダの隣に「提供ライブラリ」タブを足し、選択中は content を
  // 素材一覧の代わりに出す (中身は呼び出し側が組む。運営 / パートナー提供の効果音・フリー楽曲)。
  catalogTab?: { label: string; shortLabel: string; count: number | null; content: ReactNode };
  // 最初に開くタブ (既定は共有フォルダ)。
  initialTab?: TabType;
  // ナレーション追加ピッカー専用: フィルタに「シナリオ」(ナレーションの生成元) を出す。initialScenarioId
  // (開いているシナリオ) で絞り込んだ状態で始める。絞り込みはサーバー側 (narration_scenario_id)
  narrationScenarioFilter?: { initialScenarioId?: string };
  // 音声の一覧テーブル (audioTableLayout) の名前列の見出し (既定「名前」)
  audioTableNameLabel?: string;
}

export interface SimpleMaterialsListRef {
  scrollToTop: () => void;
  /** 現在のフィルタ状態を取得する（素材ページへの引き継ぎ用） */
  getActiveFilters: () => MaterialsFilterSnapshot;
}

const SimpleMaterialsList = forwardRef<SimpleMaterialsListRef, Props>(function SimpleMaterialsList({
  mode = 'select',
  selectedAsin,
  selectedBrandId,
  onSelectMaterial,
  bulkEditMode = 'none',
  onBulkEditComplete,
  selectedAssets = [],
  onAddAsset,
  onRemoveAsset,
  activeTab: externalActiveTab,
  onTabChange,
  imageType = 'main',
  bulkActionsTarget,
  hideFileTypeFilter = false,
  hideSourceTypeFilter = false,
  hideAspectRatioFilter = false,
  hideFileSizeBadge = false,
  hideStoryboardTab = false,
  hideAmazonComplianceFilter = false,
  initialFileTypes,
  initialAspectRatios,
  initialSourceTypes,
  showMaxDurationFilter = false,
  alwaysShowSearch = false,
  brandCutFirst = false,
  soundLogoFirst = false,
  prioritize,
  excludeMaterialIds,
  playableVideoCards = false,
  hideDetailsButton = false,
  showSelectButton = false,
  paginated = false,
  audioTableLayout = false,
  catalogTab,
  initialTab,
  narrationScenarioFilter,
  audioTableNameLabel,
}, ref) {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [internalActiveTab, setInternalActiveTab] = useState<TabType>(initialTab ?? 'cloud');
  const activeTab = externalActiveTab ?? internalActiveTab;
  // 簡素素材 UI (usodeki 等) は共有フォルダタブを出さず、フィルタも取得元のみに絞る
  const simpleMaterialsUi = brandConfig.features.simpleMaterialsUi ?? false;
  // 選択専用のピッカー (mode='select') では、開いていないタブに素材があることに気づかせるため、
  // 共有 / 個人フォルダの件数を反転 (ピル型) で出す。アクティブなタブと0件のタブは通常表示のまま。
  const tabCountClass = (isActive: boolean, count: number) =>
    mode === 'select' && !isActive && count > 0 ? 'tab-count tab-count-pill' : 'tab-count';
  const panelInfoMessage = useMaterialsPanelStore((state) => state.panelInfoMessage);
  const setPanelInfoMessage = useMaterialsPanelStore((state) => state.setPanelInfoMessage);
  // ストーリーボードタブ (3rd タブ) の状態。storyboardImageUrl が立っていれば
  // タブ表示中、null なら従来の素材2タブのみ。
  const storyboardImageUrl = useMaterialsPanelStore((state) => state.storyboardImageUrl);
  const storyboardItems = useMaterialsPanelStore((state) => state.storyboardItems);
  const isStoryboardTabVisible = !hideStoryboardTab && storyboardImageUrl != null;

  const [activeMaterial, setActiveMaterial] = useState<CloudMaterial | TemporaryMaterial | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const materialsGridRef = useRef<HTMLDivElement>(null);
  // ストーリーボードタブは素材を扱わないため storyboard キーは常に空のプレースホルダ。
  // 型を Record<TabType, ...> に揃えることで activeTab===tab のインデックス参照を型安全に保つ。
  const [deletedPlaceholdersMap, setDeletedPlaceholdersMap] = useState<Record<MaterialsTab, Record<string, PlaceholderEntry>>>({ cloud: {}, temporary: {} });
  const [displayOrderMap, setDisplayOrderMap] = useState<Record<MaterialsTab, string[]>>({ cloud: [], temporary: [] });
  const measurementsRef = useRef<Record<string, { cardHeight: number; previewHeight: number }>>({});
  // Pull-to-refresh
  const [pullState, setPullState] = useState<{ distance: number; refreshing: boolean }>({ distance: 0, refreshing: false });
  const pullStartY = useRef<number | null>(null);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);

  // URL変更を監視するステート（パス名とクエリパラメータ両方）
  const [urlState, setUrlState] = useState(() => typeof window !== 'undefined'
    ? { pathname: window.location.pathname, search: window.location.search }
    : { pathname: '', search: '' }
  );
  // 後方互換性のためにurlSearchも維持
  const urlSearch = urlState.search;

  // 素材を出すタブか ('storyboard' / 'catalog' は素材を持たない仮想タブ)
  const isMaterialsTabActive = activeTab === 'cloud' || activeTab === 'temporary';
  // 仮想タブは素材を持たないので空マップで返す。
  const currentPlaceholders = isMaterialsTabActive ? deletedPlaceholdersMap[activeTab] : {};

  const handleMeasure = useCallback((tab: MaterialsTab, materialId: string, metrics: { cardHeight: number; previewHeight: number }) => {
    measurementsRef.current[makeCompositeKey(tab, materialId)] = metrics;
  }, []);

  const addPlaceholderForMaterial = useCallback((tab: MaterialsTab, material: CloudMaterial | TemporaryMaterial) => {
    setDeletedPlaceholdersMap((prev) => {
      const tabPlaceholders = prev[tab];
      if (tabPlaceholders[material.id]) {
        return prev;
      }
      const key = makeCompositeKey(tab, material.id);
      const metrics = measurementsRef.current[key];
      return {
        ...prev,
        [tab]: {
          ...tabPlaceholders,
          [material.id]: {
            material,
            cardHeight: metrics?.cardHeight,
            previewHeight: metrics?.previewHeight,
          },
        },
      };
    });
  }, []);

  const removePlaceholderForMaterial = useCallback((tab: MaterialsTab, materialId: string) => {
    setDeletedPlaceholdersMap((prev) => {
      const tabPlaceholders = prev[tab];
      if (!tabPlaceholders[materialId]) {
        return prev;
      }
      const nextForTab = { ...tabPlaceholders };
      delete nextForTab[materialId];
      return {
        ...prev,
        [tab]: nextForTab,
      };
    });
  }, []);

  // 'storyboard' タブが選択された後に素材マップ系の callback に activeTab を渡すと
  // 型が合わないため、narrowing 専用のローカル変数を用意する。素材グリッドが
  // レンダリングされるのは activeTab !== 'storyboard' のときだけ。
  const materialsTab: MaterialsTab = isMaterialsTabActive ? activeTab : 'cloud';

  const toast = useToast();
  const ensureRasterAsset = useEnsureRasterAsset();
  const tagsQuery = useTags();
  const workflowStagesQuery = useMaterialWorkflowStages();
  const workflowStages = workflowStagesQuery.data ?? [];
  const workflowStageEnabled = workflowStages.length > 0;

  // 強制フィルタによるタブ切り替え (refetch を伴わない生のタブ変更)
  const switchTab = useCallback((tab: MaterialsTab) => {
    if (onTabChange) onTabChange(tab);
    else setInternalActiveTab(tab);
  }, [onTabChange]);

  // フィルタ状態 (検索/種別/アスペクト比/商品/ブランド/タグ/制作進捗/最大尺/Amazon適合)
  const filters = useMaterialsFilters({
    mode,
    selectedAsin,
    selectedBrandId,
    hideAmazonComplianceFilter,
    initialFileTypes,
    fileTypesLocked: hideFileTypeFilter,
    initialAspectRatios,
    initialSourceTypes,
    initialNarrationScenarioId: narrationScenarioFilter?.initialScenarioId,
    showMaxDurationFilter,
    tags: tagsQuery.data,
    activeTab,
    switchTab,
  });

  // ref経由でスクロールメソッドを公開
  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (materialsGridRef.current) {
        materialsGridRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    getActiveFilters: () => filters.getSnapshot(),
  }));

  // クラウド素材を取得
  // 無限スクロール対応のクエリ（サーバーサイドフィルタ対応）
  // paginated 時はカーソル無限スクロールを止め、offset ページングに切り替える
  const cloudQuery = useCloudMaterials(filters.serverFilters, { enabled: !paginated });
  const tempQuery = useTemporaryMaterials(filters.serverFilters, { enabled: !paginated });

  // ▼ ページ番号ジャンプ (paginated) 用
  // page は URL の ?page= を正本にする (戻る/進む・リロード対応)。
  const page = useMemo(() => {
    if (!paginated) return 1;
    const p = parseInt(new URLSearchParams(urlState.search).get('page') || '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [paginated, urlState.search]);

  const setPage = useCallback((nextPage: number) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (nextPage <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(nextPage));
    window.history.pushState({}, '', url.toString());
    setUrlState({ pathname: url.pathname, search: url.search });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // アクティブタブのみページデータを取得。両タブの件数/ファセットは meta から供給。
  const cloudPaged = useCloudMaterialsPaged(filters.serverFilters, page, paginated && activeTab === 'cloud');
  const tempPaged = useTemporaryMaterialsPaged(filters.serverFilters, page, paginated && activeTab === 'temporary');
  const pagedMeta = useMaterialsPagedMeta(filters.serverFilters, paginated);

  // ブラウザの戻る/進むで ?page= が変わったら urlState を更新し page を再導出する。
  // 既存の URL 監視 effect は externalActiveTab 指定時 (= 素材ブラウズページ) はスキップされるため、
  // paginated 用に専用の popstate リスナを張る。
  useEffect(() => {
    if (!paginated || typeof window === 'undefined') return;
    const onPop = () => setUrlState({ pathname: window.location.pathname, search: window.location.search });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [paginated]);

  // フィルタ変更時はページを 1 に戻す (初回レンダーは除く)。
  const serverFiltersKey = JSON.stringify(filters.serverFilters ?? {});
  const prevFiltersKeyRef = useRef(serverFiltersKey);
  useEffect(() => {
    if (!paginated) return;
    if (prevFiltersKeyRef.current === serverFiltersKey) return;
    prevFiltersKeyRef.current = serverFiltersKey;
    setPage(1);
  }, [paginated, serverFiltersKey, setPage]);
  // ▲ ページネーション用

  // インフォメッセージは、現在のタブのデータ取得が完了したら自動でクリアする
  useEffect(() => {
    if (!panelInfoMessage) return;
    const activeQuery = activeTab === 'cloud' ? cloudQuery : tempQuery;
    if (!activeQuery.isFetching) {
      setPanelInfoMessage(null);
    }
  }, [panelInfoMessage, activeTab, cloudQuery.isFetching, tempQuery.isFetching, setPanelInfoMessage]);

  // ページを平坦化してすべてのアイテムを取得
  const cloudMaterials = useMemo(() => {
    if (paginated) return cloudPaged.data?.items ?? [];
    return cloudQuery.data?.pages.flatMap(pg => pg.items) ?? [];
  }, [paginated, cloudPaged.data, cloudQuery.data]);

  const temporaryMaterials = useMemo(() => {
    if (paginated) return tempPaged.data?.items ?? [];
    return tempQuery.data?.pages.flatMap(pg => pg.items) ?? [];
  }, [paginated, tempPaged.data, tempQuery.data]);

  // total_countとファセットデータを取得
  //   paginated: 両タブの件数/ファセットは meta から (現在ページに依存しない)
  //   それ以外 : 無限スクロールの最初のページから
  const cloudTotalCount = paginated
    ? (pagedMeta.cloud.data?.total_count ?? 0)
    : (cloudQuery.data?.pages[0]?.total_count ?? 0);
  const tempTotalCount = paginated
    ? (pagedMeta.temporary.data?.total_count ?? 0)
    : (tempQuery.data?.pages[0]?.total_count ?? 0);
  const cloudFacets = paginated ? pagedMeta.cloud.data?.facets : cloudQuery.data?.pages[0]?.facets;
  const tempFacets = paginated ? pagedMeta.temporary.data?.facets : tempQuery.data?.pages[0]?.facets;

  // アクティブタブの総ページ数 (ページネーション UI 用)
  const activeTotalCount = activeTab === 'cloud' ? cloudTotalCount : tempTotalCount;
  const totalPages = Math.max(1, Math.ceil(activeTotalCount / PAGE_LIMIT));
  const isFetchingActivePaged = activeTab === 'cloud' ? cloudPaged.isFetching : tempPaged.isFetching;

  // 両フォルダのファセットをマージ（いずれかに存在すれば選択肢に表示）
  const facets = useMemo(() => {
    if (!cloudFacets && !tempFacets) return undefined;
    const cf = cloudFacets;
    const tf = tempFacets;
    return {
      source_types: [...new Set([...(cf?.source_types ?? []), ...(tf?.source_types ?? [])])],
      has_no_source_items: (cf?.has_no_source_items ?? false) || (tf?.has_no_source_items ?? false),
      aspect_ratios: [...new Set([...(cf?.aspect_ratios ?? []), ...(tf?.aspect_ratios ?? [])])],
      brand_ids: [...new Set([...(cf?.brand_ids ?? []), ...(tf?.brand_ids ?? [])])],
      has_no_brand_items: (cf?.has_no_brand_items ?? false) || (tf?.has_no_brand_items ?? false),
      has_no_product_items: (cf?.has_no_product_items ?? false) || (tf?.has_no_product_items ?? false),
      tag_ids: [...new Set([...(cf?.tag_ids ?? []), ...(tf?.tag_ids ?? [])])],
      workflow_stage_ids: [...new Set([...(cf?.workflow_stage_ids ?? []), ...(tf?.workflow_stage_ids ?? [])])],
      has_no_workflow_stage_items: (cf?.has_no_workflow_stage_items ?? false) || (tf?.has_no_workflow_stage_items ?? false),
    };
  }, [cloudFacets, tempFacets]);

  // シナリオ絞り込みの選択肢 (表示中のフォルダのファセット。ブランド / 商品などシナリオ以外の条件を掛けた件数。
  // 絞り込み中のシナリオは 0 件でも残る)
  const activeFacets = activeTab === 'cloud' ? cloudFacets : tempFacets;
  const narrationScenarioOptions = useMemo(
    () =>
      narrationScenarioFilter
        ? (activeFacets?.narration_scenarios ?? []).map((s) => ({ value: s.id, label: s.title || '(無題のシナリオ)', badge: s.count }))
        : undefined,
    [narrationScenarioFilter, activeFacets],
  );

  // 取得元フィルタの選択肢。共有マップ (buildSourceFilterGroups) を正本にし、
  // ファセットに実在する source_type だけをカテゴリ順で並べる。各カテゴリの先頭に区切り線。
  // ファセット未取得の間は既知の全 source_type を出す。
  const sourceFilterOptions = useMemo(() => {
    const available = facets?.source_types ?? Object.keys(SOURCE_TYPE_LABELS);
    const opts: { value: string; label: string; divider?: boolean }[] = [];
    if (facets?.has_no_source_items !== false) {
      opts.push({ value: '__no_source__', label: '取得元なし' });
    }
    for (const group of buildSourceFilterGroups(available)) {
      group.options.forEach((o, oi) => {
        // カテゴリ境界 (各グループ先頭) かサブグループ先頭 (o.divider) で区切り線
        const divider = (oi === 0 && opts.length > 0) || !!o.divider;
        opts.push(divider ? { ...o, divider: true } : { ...o, divider: false });
      });
    }
    return opts;
  }, [facets?.source_types, facets?.has_no_source_items]);

  // 互換性のために既存の変数名を維持 (paginated 時はアクティブタブの paged クエリを参照)
  const isLoadingCloud = paginated
    ? (cloudPaged.isLoading && !cloudPaged.isPlaceholderData)
    : (cloudQuery.isLoading && !cloudQuery.isPlaceholderData);
  const isFetchingCloud = paginated ? cloudPaged.isFetching : cloudQuery.isFetching;
  const errorCloud = paginated ? cloudPaged.error : cloudQuery.error;
  const refetchCloud = paginated ? cloudPaged.refetch : cloudQuery.refetch;

  const isLoadingTemp = paginated
    ? (tempPaged.isLoading && !tempPaged.isPlaceholderData)
    : (tempQuery.isLoading && !tempQuery.isPlaceholderData);
  const isFetchingTemp = paginated ? tempPaged.isFetching : tempQuery.isFetching;
  const errorTemp = paginated ? tempPaged.error : tempQuery.error;
  const refetchTemp = paginated ? tempPaged.refetch : tempQuery.refetch;

  // Pull-to-refresh: モバイルのみ
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const refetchCloudRef = useRef(refetchCloud);
  refetchCloudRef.current = refetchCloud;
  const refetchTempRef = useRef(refetchTemp);
  refetchTempRef.current = refetchTemp;

  useEffect(() => {
    // paginated (ページ番号ジャンプ) ではグリッドが内部スクロールしないため pull-to-refresh は無効
    if (!isMobile || paginated) return;
    const el = materialsGridRef.current;
    if (!el) return;

    const THRESHOLD = 60;

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop <= 0 && !isRefreshingRef.current) {
        pullStartY.current = e.touches[0].clientY;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (pullStartY.current === null) return;
      const diff = e.touches[0].clientY - pullStartY.current;
      if (diff > 0 && el.scrollTop <= 0) {
        e.preventDefault();
        const dist = Math.min(diff * 0.5, THRESHOLD + 20);
        pullDistanceRef.current = dist;
        setPullState({ distance: dist, refreshing: false });
      } else {
        pullStartY.current = null;
        pullDistanceRef.current = 0;
        setPullState({ distance: 0, refreshing: false });
      }
    };

    const onTouchEnd = async () => {
      if (pullStartY.current === null) return;
      pullStartY.current = null;
      if (pullDistanceRef.current >= THRESHOLD && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        setPullState({ distance: 40, refreshing: true });
        try {
          if (activeTabRef.current === 'cloud') {
            await refetchCloudRef.current();
          } else {
            await refetchTempRef.current();
          }
        } finally {
          isRefreshingRef.current = false;
          pullDistanceRef.current = 0;
          setPullState({ distance: 0, refreshing: false });
        }
      } else {
        pullDistanceRef.current = 0;
        setPullState({ distance: 0, refreshing: false });
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, paginated]);

  // フィルタドロップダウンの選択肢 (Amazon/Manual 商品・ブランド・タグ)
  const options = useFilterOptions({
    brandFilter: filters.brandFilter,
    productFilter: filters.productFilter,
    facets,
    tags: tagsQuery.data,
  });

  // Amazon商品画像の仕様を取得
  // メイン画像: 4b867d5d-5186-42c3-89be-b0f86c7d85ba
  // サブ画像: 705fe05e-9ac2-440e-85f6-cc1953f591e0
  const AMAZON_MAIN_IMAGE_SPEC_ID = '4b867d5d-5186-42c3-89be-b0f86c7d85ba';
  const AMAZON_SUB_IMAGE_SPEC_ID = '705fe05e-9ac2-440e-85f6-cc1953f591e0';

  // imageTypeに基づいて適切な仕様IDを選択
  const currentSpecId = imageType === 'main' ? AMAZON_MAIN_IMAGE_SPEC_ID : AMAZON_SUB_IMAGE_SPEC_ID;

  const { data: amazonImageSpec } = useQuery<ImageAssetSpec | null>({
    queryKey: ['image-spec', currentSpecId],
    queryFn: () => fetchImageSpecById(currentSpecId),
    enabled: filters.amazonProductImageFilter, // フィルタがOnの時のみ取得
    staleTime: 10 * 60 * 1000, // 10分間はキャッシュを使用
  });

  // タブ切り替え時にデータを再取得
  const setActiveTab = (tab: ControlledTabType) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }

    // タブ切り替え時にデータを再取得
    if (tab === 'cloud') {
      refetchCloud();
    } else {
      refetchTemp();
    }
  };

  // ユーザーによるタブクリック: URL も /materials/shared or /materials/personal に揃え、
  // 素材詳細モーダルが開いていれば閉じる
  const handleTabClick = (tab: ControlledTabType) => {
    // 素材モーダルを閉じる
    setActiveMaterial(null);
    setActiveIndex(null);

    setActiveTab(tab);

    // URL を更新（pushState フックを介して RightPanelWrapper も同期される）
    if (typeof window !== 'undefined') {
      const targetPath = tab === 'temporary' ? '/materials/personal' : '/materials/shared';
      const currentPath = window.location.pathname;
      // 素材詳細系のクエリパラメータを除去
      const params = new URLSearchParams(window.location.search);
      params.delete('material_id');
      params.delete('detail');
      params.delete('folder');
      params.delete('folder_type');
      // タブ切替時はページ番号を 1 に戻す (paginated: Q1)
      params.delete('page');
      const search = params.toString();
      const newUrl = `${targetPath}${search ? `?${search}` : ''}`;
      if (currentPath.startsWith('/materials/')) {
        window.history.pushState({}, '', newUrl);
        setUrlState({
          pathname: targetPath,
          search: search ? `?${search}` : '',
        });
      }
    }
  };

  // タブに応じて素材を切り替え
  const materials = activeTab === 'cloud' ? cloudMaterials : temporaryMaterials;
  const isLoading = activeTab === 'cloud' ? isLoadingCloud : isLoadingTemp;
  const error = activeTab === 'cloud' ? errorCloud : errorTemp;

  // ラスタ版を取得中の素材 ID (SVG 等)。取得の完了を待たずに「選択中」を出し、失敗したら外す
  const [rasterPendingIds, setRasterPendingIds] = useState<ReadonlySet<string>>(() => new Set());
  const setRasterPending = useCallback((id: string, pending: boolean) => {
    setRasterPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // 選択状態マップを作成
  const selectedMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    rasterPendingIds.forEach((id) => {
      map[id] = true;
    });
    selectedAssets.forEach((asset) => {
      if (activeTab === 'cloud' && asset.type === 'cloud') {
        map[asset.id] = true;
      } else if (activeTab === 'temporary' && asset.type === 'temporary') {
        map[asset.id] = true;
      }
    });
    return map;
  }, [selectedAssets, activeTab, rasterPendingIds]);

  // クライアントサイドフィルタ（Amazon商品画像適合フィルタのみ）
  // 他のフィルタはサーバーサイドで処理される
  const amazonProductImageFilter = filters.amazonProductImageFilter;
  const filterMaterial = useCallback((material: CloudMaterial | TemporaryMaterial) => {
    // Amazon商品画像適合フィルタ（複雑な条件のためクライアントサイドで処理）
    if (amazonProductImageFilter && amazonImageSpec) {
      const contentType = material.content_type || '';
      // 画像以外（動画など）は除外
      if (!contentType.startsWith('image/')) {
        return false;
      }
      // 画像の場合は仕様に適合するかチェック
      if (!checkMaterialMatchesSpec(material, amazonImageSpec)) {
        return false;
      }
    }

    return true;
  }, [amazonProductImageFilter, amazonImageSpec]);

  // 除外IDセット（既に紐付け済みの素材をピッカーから隠す用途）。参照を安定させる。
  const excludeIdSet = useMemo(
    () => new Set(excludeMaterialIds ?? []),
    [excludeMaterialIds],
  );

  const filteredMaterials = useMemo(
    () =>
      materials.filter(
        (m) => filterMaterial(m) && !excludeIdSet.has(m.id),
      ),
    [materials, filterMaterial, excludeIdSet],
  );


  // ASIN優先ソート（selectモード時はselectedAsinで、商品フィルタでAmazon選択時はそのASIN）
  // サーバーサイドのソート順（display_order優先、created_at降順）を維持しながら、
  // ASINマッチするものを優先表示
  const targetAsinForSort = useMemo(() => {
    if (mode === 'select' && selectedAsin) return selectedAsin;
    if (filters.productFilter && filters.productFilter !== '__no_product__') {
      const [type, id] = filters.productFilter.split(':');
      if (type === 'amazon') {
        const amazonProducts = options.amazonProductsData?.products || [];
        const product = amazonProducts.find((p: any) => p.id === id);
        return product?.asin || null;
      }
    }
    return null;
  }, [mode, selectedAsin, filters.productFilter, options.amazonProductsData]);
  // ブランドカット優先ソート (タイトル/エンドカードの素材ピッカー専用) と ASIN 優先ソートを
  // 多段で適用する。いずれも有効でなければサーバーソート順をそのまま使う。
  const asinLowerTarget = targetAsinForSort && targetAsinForSort !== '__no_asin__'
    ? targetAsinForSort.toLowerCase()
    : null;
  // paginated 時はクライアント再整列を無効化する。OFFSET/LIMIT 後にページ内だけ
  // 並べ替えるとページ境界 (どの素材が何ページ目か) が崩れるため、サーバー順をそのまま使う。
  const sortedMaterials = (!paginated && (brandCutFirst || soundLogoFirst || asinLowerTarget || prioritize))
    ? [...filteredMaterials].map((item, index) => ({ item, originalIndex: index }))
        .sort((a, b) => {
          // 0. 呼び出し側の採点 (スクロール動画の背景: 枠 / 位置 / 比率が合う素材を上に)
          if (prioritize) {
            const diff = prioritize(b.item) - prioritize(a.item);
            if (diff !== 0) return diff;
          }
          // 1. ブランドカット最優先
          if (brandCutFirst) {
            const aCut = isBrandCutMaterial(a.item);
            const bCut = isBrandCutMaterial(b.item);
            if (aCut !== bCut) return aCut ? -1 : 1;
          }
          // 1'. サウンドロゴ最優先 (シーンのサウンドロゴ選択ピッカー用)
          if (soundLogoFirst) {
            const aLogo = isSoundLogoMaterial(a.item);
            const bLogo = isSoundLogoMaterial(b.item);
            if (aLogo !== bLogo) return aLogo ? -1 : 1;
          }
          // 2. ASINマッチ優先
          if (asinLowerTarget) {
            const aMatches = normalizeAsinForDisplay(a.item.asin).toLowerCase().includes(asinLowerTarget);
            const bMatches = normalizeAsinForDisplay(b.item.asin).toLowerCase().includes(asinLowerTarget);
            if (aMatches && !bMatches) return -1;
            if (!aMatches && bMatches) return 1;
          }
          // 3. 同条件ならサーバーソート順を維持（stable sort）
          return a.originalIndex - b.originalIndex;
        })
        .map(({ item }) => item)
    : filteredMaterials;

  // 一括操作 (削除/移動/タグ/商品関連付け) の状態とハンドラ
  const bulk = useBulkMaterialActions({
    bulkEditMode,
    onBulkEditComplete,
    materials,
    materialsTab,
    activeTab,
    addPlaceholderForMaterial,
    removePlaceholderForMaterial,
  });
  const { bulkSelectedIds, setBulkSelectedIds, deleteCloudMutation, deleteTemporaryMutation } = bulk;

  useEffect(() => {
    // 仮想タブ ('storyboard' / 'catalog') は素材を扱わないので順序マップは更新しない
    if (!isMaterialsTabActive) return;
    setDisplayOrderMap((prev) => {
      const prevOrder = prev[materialsTab] ?? [];
      const placeholderIds = Object.keys(currentPlaceholders);
      const sortedIds = sortedMaterials.map((item) => item.id);

      // 新しい順序: sortedIds（サーバーサイドソート順）を基準にする
      const nextOrder: string[] = [...sortedIds, ...placeholderIds];

      // 順序が変わっていなければ更新しない（無限ループ防止）
      if (nextOrder.length === prevOrder.length && nextOrder.every((id, index) => id === prevOrder[index])) {
        return prev;
      }

      return {
        ...prev,
        [materialsTab]: nextOrder,
      };
    });
  }, [sortedMaterials, currentPlaceholders, isMaterialsTabActive, materialsTab]);

  // popstate/pushState/replaceStateイベントを監視してURL変更を検知
  // 外部からタブが制御されている場合（RightPanelWrapper経由）は、RightPanelWrapperがURL監視を担当するためスキップ
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // 外部からタブが制御されている場合はスキップ
    if (externalActiveTab !== undefined) return;

    const updateUrlState = () => {
      setUrlState({
        pathname: window.location.pathname,
        search: window.location.search,
      });
    };

    // popstateイベント（ブラウザの戻る/進むボタン）
    const handlePopState = () => {
      updateUrlState();
    };
    window.addEventListener('popstate', handlePopState);

    // pushState/replaceStateをフック（React Routerなどが使用）
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      updateUrlState();
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      updateUrlState();
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [externalActiveTab]);

  // URLパス・パラメータを監視して、適切なタブに切り替え
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const path = urlState.pathname;
    const searchParams = new URLSearchParams(urlState.search);
    const materialId = searchParams.get('material_id');
    const folderType = searchParams.get('folder_type') as TabType | null;

    const handleTabChangeInternal = onTabChange || ((tab: TabType) => setInternalActiveTab(tab));

    // パス・folder_typeベースのタブ切り替えは、外部制御時はRightPanelWrapperが担当するためスキップ
    if (externalActiveTab === undefined) {
      // 新URL構造: /materials/personal → temporary タブ
      if (path.startsWith('/materials/personal')) {
        if (activeTab !== 'temporary') {
          handleTabChangeInternal('temporary');
        }
        return;
      }
      // 新URL構造: /materials/shared → cloud タブ
      if (path.startsWith('/materials/shared')) {
        if (activeTab !== 'cloud') {
          handleTabChangeInternal('cloud');
        }
        return;
      }

      // 旧URL互換: folder_type パラメータがある場合
      if (folderType === 'cloud' || folderType === 'temporary') {
        if (activeTab !== folderType) {
          handleTabChangeInternal(folderType);
        }
        return;
      }
    }

    // material_id があるが folder_type がない場合は従来の方法（データから検索）
    // ※ 外部制御時もmaterial_idベースのタブ切り替えは実行する
    if (!materialId) return;

    // 両方のデータが読み込まれるまで待つ
    if (cloudQuery.isLoading || tempQuery.isLoading) return;

    // CloudMaterials で素材を検索
    const cloudMaterialsData = Array.isArray(cloudQuery.data?.pages)
      ? cloudQuery.data.pages.flatMap((page: any) => page.items || [])
      : [];
    const foundInCloud = cloudMaterialsData.some((m: any) => m.id === materialId);

    // TemporaryMaterials で素材を検索
    const tempMaterials = Array.isArray(tempQuery.data?.pages)
      ? tempQuery.data.pages.flatMap((page: any) => page.items || [])
      : [];
    const foundInTemp = tempMaterials.some((m: any) => m.id === materialId);

    // 該当するタブに切り替え
    if (foundInCloud && activeTab !== 'cloud') {
      handleTabChangeInternal('cloud');
    } else if (foundInTemp && activeTab !== 'temporary') {
      handleTabChangeInternal('temporary');
    }
  }, [urlState.pathname, urlState.search, cloudQuery.data, tempQuery.data, cloudQuery.isLoading, tempQuery.isLoading, activeTab, onTabChange, externalActiveTab]);

  // URLパラメータ（detail または material_id）を監視して、該当素材のモーダルを開く
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(urlSearch);
    // 新URL: detail パラメータ、旧URL: material_id パラメータ
    const materialId = searchParams.get('detail') || searchParams.get('material_id');

    if (!materialId) {
      return;
    }

    // データ読み込み中の場合は待つ
    if (cloudQuery.isLoading || tempQuery.isLoading) {
      return;
    }

    // 一覧から検索
    const material = sortedMaterials.find((m) => m.id === materialId);
    if (material) {
      const index = sortedMaterials.findIndex((m) => m.id === materialId);
      setActiveMaterial(material);
      setActiveIndex(index >= 0 ? index : null);
      return;
    }

    // 一覧に見つからない場合、APIから直接取得してモーダルを開く
    if (!cloudQuery.isLoading && !tempQuery.isLoading) {
      fetchMaterialById(materialId).then((fetched) => {
        if (fetched) {
          // paginated では現在ページにない素材 (別ページ/別タブ) を深いリンクで開くのが常態。
          // 取得物の folder_type が現タブと異なれば、モーダル分岐 (activeTab 依存) を合わせるため
          // タブを切り替える。activeIndex は null のまま (現ページに無いので左右ナビ無効)。
          const fetchedFolder = (fetched as { folder_type?: ControlledTabType }).folder_type;
          if (paginated && fetchedFolder && fetchedFolder !== activeTab) {
            setActiveTab(fetchedFolder);
          }
          setActiveMaterial(fetched);
          setActiveIndex(null);
        }
      });
    }
  }, [urlSearch, sortedMaterials, cloudQuery.isLoading, tempQuery.isLoading]);

  // ストア経由の素材詳細モーダル開放リクエストを監視
  const requestDetailMaterialId = useMaterialsPanelStore((s) => s.requestDetailMaterialId);
  const clearDetailRequest = useMaterialsPanelStore((s) => s.clearDetailRequest);
  useEffect(() => {
    if (!requestDetailMaterialId) return;
    if (sortedMaterials.length === 0) return;
    const material = sortedMaterials.find((m) => m.id === requestDetailMaterialId);
    if (material) {
      const index = sortedMaterials.findIndex((m) => m.id === requestDetailMaterialId);
      setActiveMaterial(material);
      setActiveIndex(index >= 0 ? index : null);
      clearDetailRequest();
    }
  }, [requestDetailMaterialId, sortedMaterials, clearDetailRequest]);

  useEffect(() => {
    setDeletedPlaceholdersMap((prev) => {
      const placeholders = prev[materialsTab] ?? {};
      if (!Object.keys(placeholders).length) {
        return prev;
      }
      let changed = false;
      const nextForTab = { ...placeholders };
      sortedMaterials.forEach((material) => {
        if (nextForTab[material.id]) {
          delete nextForTab[material.id];
          changed = true;
        }
      });
      if (!changed) {
        return prev;
      }
      return {
        ...prev,
        [materialsTab]: nextForTab,
      };
    });
  }, [sortedMaterials, materialsTab]);

  // アスペクト比に基づいてグリッドのサイズを計算
  const getGridSize = useCallback((material: CloudMaterial | TemporaryMaterial) => {
    const { width, height } = material;
    if (!width || !height) return { columnSpan: 1, rowSpan: 1, imageHeight: 150 };

    const ratio = width / height;

    // usodeki は情報ペイン(約60px)を隠すため、行高を 150px に詰める
    // (usodeki-theme.css の grid-auto-rows と一致させる)。縦2ブロックの画像高も
    // 2*150 + gap(16) = 316px に合わせる。他ブランドは従来通り 210px 行・360px。
    const tallImageHeight = simpleMaterialsUi ? 316 : 360;

    // 16:9 (1.78) または 1.91:1 → 横2ブロック
    if (ratio >= 1.6 && ratio <= 2.0) {
      return { columnSpan: 2, rowSpan: 1, imageHeight: 150 };
    }
    // 9:16 (0.56) または 1:1.91 (0.52) → 縦2ブロック
    // 2ブロック分の高さ(420px) - 情報部(60px) = 360px
    if (ratio >= 0.5 && ratio <= 0.65) {
      return { columnSpan: 1, rowSpan: 2, imageHeight: tallImageHeight };
    }
    // 1:1 または その他 → 1ブロック
    return { columnSpan: 1, rowSpan: 1, imageHeight: 150 };
  }, [simpleMaterialsUi]);

  const displayMaterials = useMemo<DisplayEntry[]>(() => {
    const placeholders = deletedPlaceholdersMap[materialsTab] ?? {};
    const order = displayOrderMap[materialsTab] ?? [];
    const entries: DisplayEntry[] = [];
    const rawMap = new Map(sortedMaterials.map((item) => [item.id, item]));
    const seen = new Set<string>();

    order.forEach((id) => {
      const placeholder = placeholders[id];
      if (placeholder) {
        seen.add(id);
        if (filterMaterial(placeholder.material)) {
          entries.push({ id, material: placeholder.material, isDeleted: true, placeholder });
        }
        return;
      }

      const material = rawMap.get(id);
      if (material) {
        seen.add(id);
        entries.push({ id, material, isDeleted: false });
      }
    });

    sortedMaterials.forEach((material) => {
      if (seen.has(material.id)) {
        return;
      }
      entries.push({ id: material.id, material, isDeleted: false });
    });

    Object.entries(placeholders).forEach(([id, placeholder]) => {
      if (seen.has(id)) {
        return;
      }
      if (filterMaterial(placeholder.material)) {
        entries.push({ id, material: placeholder.material, isDeleted: true, placeholder });
      }
    });

    return entries;
  }, [materialsTab, deletedPlaceholdersMap, displayOrderMap, sortedMaterials, filterMaterial]);

  // GCS素材の署名付きURLを管理
  const gcsSignedUrls = useGcsSignedUrlMap(displayMaterials, activeTab);

  // ナビゲーション用の素材リスト（削除済みを除外、表示順序に従う）
  const navigableMaterials = useMemo(() => {
    return displayMaterials
      .filter(({ isDeleted }) => !isDeleted)
      .map(({ material }) => material);
  }, [displayMaterials]);

  // 詳細モーダルを閉じる（URL の詳細パラメータもクリア）
  const closeDetailModal = () => {
    setActiveMaterial(null);
    setActiveIndex(null);
    clearDetailRequest();
    useMaterialsPanelStore.getState().dismissProductLinkCoachMark();
    // URLパラメータをクリア & urlStateも更新
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('detail');
      url.searchParams.delete('folder');
      url.searchParams.delete('material_id');
      url.searchParams.delete('folder_type');
      window.history.replaceState({}, '', url.toString());
      setUrlState({
        pathname: url.pathname,
        search: url.search,
      });
    }
  };

  // 表示中の素材 ID を URL に反映する（既にパラメータがある場合のみ）。
  // 削除後に次の素材へ送ったあと、URL 由来の復元 effect が消えた素材へ戻すのを防ぐ。
  const syncDetailUrlParam = (materialId: string) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    let changed = false;
    (['detail', 'material_id'] as const).forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, materialId);
        changed = true;
      }
    });
    if (!changed) return;
    window.history.replaceState({}, '', url.toString());
    setUrlState({
      pathname: url.pathname,
      search: url.search,
    });
  };

  // モーダルの左右ナビ。位置は表示中の素材 ID から引き直す
  // (削除直後は activeIndex が一覧とズレるため、ID を正とする)
  const navigateDetail = (direction: 1 | -1) => {
    if (!navigableMaterials.length) return;
    const indexById = activeMaterial
      ? navigableMaterials.findIndex((mat) => mat.id === activeMaterial.id)
      : -1;
    const baseIndex = indexById >= 0 ? indexById : activeIndex;
    // 深いリンク等で現在ページに無い素材を単体表示中 (activeIndex===null) は左右ナビ無効
    if (baseIndex === null || baseIndex < 0) return;
    const nextIndex = (baseIndex + direction + navigableMaterials.length) % navigableMaterials.length;
    setActiveIndex(nextIndex);
    setActiveMaterial(navigableMaterials[nextIndex] ?? null);
  };

  // モーダルから削除したときは閉じずに次の素材を表示する (最後の 1 件なら閉じる)
  const handleDetailDeleted = (deletedId: string) => {
    const next = pickNextMaterialAfterDelete(navigableMaterials, deletedId, activeIndex);
    if (!next) {
      closeDetailModal();
      return;
    }
    setActiveIndex(next.index);
    setActiveMaterial(next.material);
    syncDetailUrlParam(next.material.id);
  };

  // データ更新（refetch）
  const refetch = async () => {
    try {
      if (activeTab === 'cloud') {
        // 削除済みプレースホルダと表示順序をクリア
        setDeletedPlaceholdersMap(prev => ({ ...prev, cloud: {} }));
        setDisplayOrderMap(prev => ({ ...prev, cloud: [] }));
        // キャッシュを完全にリセットしてから再取得（無限スクロールのページデータをクリア）
        await queryClient.resetQueries({ queryKey: ['cloud-materials'] });
      } else {
        // 削除済みプレースホルダと表示順序をクリア
        setDeletedPlaceholdersMap(prev => ({ ...prev, temporary: {} }));
        setDisplayOrderMap(prev => ({ ...prev, temporary: [] }));
        // キャッシュを完全にリセットしてから再取得（無限スクロールのページデータをクリア）
        await queryClient.resetQueries({ queryKey: ['temporary-materials'] });
      }
    } catch (error) {
      console.error('素材情報の更新に失敗しました:', error);
      toast.error('素材情報の更新に失敗しました');
    }
  };

  return (
    <div className={`simple-materials-list${paginated ? ' paginated' : ''}`}>
      {/* タブ切り替え - 常に表示。ストーリーボードタブが出ているときも
          共有/個人タブはラベルを保ち、3 タブで幅を 3 分割する。
          usodeki はフォルダタブを出さない (中身の素材グリッドは下にそのまま表示)。 */}
      {(!simpleMaterialsUi || isStoryboardTabVisible || catalogTab) && (
      <div className={`materials-tabs${isStoryboardTabVisible || catalogTab ? ' has-storyboard-tab' : ''}`}>
        {/* usodeki (フォルダタブ無し) でも提供ライブラリタブを出すときは、素材側へ戻るタブを 1 つ置く */}
        {simpleMaterialsUi && catalogTab && (
        <button
          type="button"
          className={`tab-button ${isMaterialsTabActive ? 'active' : ''}`}
          onClick={() => setActiveTab('cloud')}
        >
          <span className="tab-label">素材</span>
        </button>
        )}
        {!simpleMaterialsUi && (
        <button
          type="button"
          className={`tab-button ${activeTab === 'cloud' ? 'active' : ''}`}
          onClick={() => handleTabClick('cloud')}
        >
          <span className="tab-label">
            <img src="/cloud_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
            <span className="tab-label-full">共有フォルダ</span>
            <span className="tab-label-short">共有</span>
          </span>
          <span className={tabCountClass(activeTab === 'cloud', cloudTotalCount)}><span className="tab-count-number">{cloudTotalCount >= 1000 ? '999+' : cloudTotalCount}</span><span className="tab-count-unit">件</span></span>
        </button>
        )}
        {!simpleMaterialsUi && (
        <button
          type="button"
          className={`tab-button tab-personal ${activeTab === 'temporary' ? 'active' : ''}`}
          onClick={() => handleTabClick('temporary')}
        >
          <span className="tab-label">
            <img src="/single_neutral_folder_box.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
            <span className="tab-label-full">個人フォルダ</span>
            <span className="tab-label-short">個人</span>
          </span>
          <span className={tabCountClass(activeTab === 'temporary', tempTotalCount)}><span className="tab-count-number">{tempTotalCount >= 1000 ? '999+' : tempTotalCount}</span><span className="tab-count-unit">件</span></span>
        </button>
        )}
        {isStoryboardTabVisible && (
          <button
            type="button"
            className={`tab-button tab-storyboard ${activeTab === 'storyboard' ? 'active' : ''}`}
            onClick={() => {
              if (onTabChange) onTabChange('storyboard');
              else setInternalActiveTab('storyboard');
            }}
          >
            <span className="tab-label">
              <img src="/film_line.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
              <span className="tab-label-full">ストーリーボード</span>
              <span className="tab-label-short">ストーリー</span>
            </span>
            <span className="tab-count"><span className="tab-count-number">{storyboardItems.length >= 1000 ? '999+' : storyboardItems.length}</span><span className="tab-count-unit">案</span></span>
          </button>
        )}
        {catalogTab && (
          <button
            type="button"
            className={`tab-button tab-catalog ${activeTab === 'catalog' ? 'active' : ''}`}
            onClick={() => setInternalActiveTab('catalog')}
          >
            <span className="tab-label">
              <span className="tab-label-full">{catalogTab.label}</span>
              <span className="tab-label-short">{catalogTab.shortLabel}</span>
            </span>
            {catalogTab.count != null && (
              <span className="tab-count"><span className="tab-count-number">{catalogTab.count >= 1000 ? '999+' : catalogTab.count}</span><span className="tab-count-unit">件</span></span>
            )}
          </button>
        )}
      </div>
      )}

      {/* 提供ライブラリタブが選択中: 素材一覧の代わりに呼び出し側の中身を出す */}
      {activeTab === 'catalog' && catalogTab?.content}

      {/* ストーリーボードタブが選択中: 素材グリッドの代わりに絵コンテ画像を表示 */}
      {activeTab === 'storyboard' && <StoryboardTabPane />}

      {/* ストーリーボードタブ以外: 通常の素材一覧 UI (バナー/ローディング/フィルタ/グリッド) */}
      {isMaterialsTabActive && (<>

      {/* インフォメッセージ（共有→個人フォルダのフォールバック等） */}
      {panelInfoMessage && (
        <div className="materials-info-banner" role="status">
          <span className="materials-info-banner-spinner" aria-hidden="true" />
          <span>{panelInfoMessage}</span>
        </div>
      )}

      {/* ローディング状態 */}
      {isLoading && (
        <div className="simple-materials-loading">
          <div className="spinner"></div>
          <p>素材を読み込み中...</p>
        </div>
      )}

      {/* エラー状態 */}
      {error && !isLoading && (
        <div className="simple-materials-error">
          <p>素材の取得に失敗しました</p>
          <button
            type="button"
            className="btn-modal-secondary"
            onClick={() => {
              if (activeTab === 'cloud') {
                refetchCloud();
              } else {
                refetchTemp();
              }
            }}
            style={{ marginTop: '12px' }}
          >
            再試行
          </button>
        </div>
      )}

      {/* フィルタセクション - ローディング・エラー時は非表示 */}
      {!isLoading && !error && (
      <>
      {/* usodeki は取得元フィルタのみ (中高生向けに絞り込みを最小化) */}
      <MaterialsFilterBar
        filters={filters}
        options={options}
        facets={facets}
        sourceFilterOptions={sourceFilterOptions}
        workflowStages={workflowStages}
        workflowStageEnabled={workflowStageEnabled}
        mode={mode}
        imageType={imageType}
        hideFileTypeFilter={hideFileTypeFilter}
        hideSourceTypeFilter={hideSourceTypeFilter}
        hideAspectRatioFilter={hideAspectRatioFilter}
        hideAmazonComplianceFilter={hideAmazonComplianceFilter}
        showMaxDurationFilter={showMaxDurationFilter}
        alwaysShowSearch={alwaysShowSearch}
        sourceOnly={simpleMaterialsUi}
        narrationScenarioOptions={narrationScenarioOptions}
        onRefresh={() => refetch()}
        isRefreshing={isFetchingCloud || isFetchingTemp}
      />
          {/* 選択操作ボタン */}
          {bulkEditMode !== 'none' && (
            <div className="selection-actions">
              <div className="selection-actions-left">
                <button
                  type="button"
                  className="selection-action-button"
                  onClick={() => {
                    const allIds = new Set(sortedMaterials.map(m => m.id));
                    setBulkSelectedIds(allIds);
                  }}
                  disabled={sortedMaterials.length === 0}
                >
                  {paginated ? 'このページを全選択' : 'すべて選択'}
                </button>
                <button
                  type="button"
                  className="selection-action-button"
                  onClick={() => setBulkSelectedIds(new Set())}
                  disabled={bulkSelectedIds.size === 0}
                >
                  選択解除
                </button>
              </div>
            </div>
          )}
      </>
      )}

      {/* 空の状態 */}
      {!isLoading && !error && (
        displayMaterials.length === 0 ? (
        <div className="simple-materials-empty">
          <p>素材が見つかりませんでした</p>
          {(selectedAsin || filters.hasActiveFilters) && (
            <button type="button" className="btn-modal-secondary" onClick={filters.clearFilters}>
              フィルタをリセット
            </button>
          )}
        </div>
      ) : mode === 'select' && (audioTableLayout === true || (audioTableLayout === 'auto'
          && displayMaterials.every(({ material }) => material.content_type?.startsWith('audio/')))) ? (
        <div className="materials-audio-table">
          <AudioTrackTable
            nameLabel={audioTableNameLabel}
            libraryLabel="取得元"
            rowTestId="materials-audio-row"
            rows={displayMaterials
              .filter(({ isDeleted }) => !isDeleted)
              .map(({ id, material }) => {
                const urls = resolveMaterialUrls(material, gcsSignedUrls.get(id) || null);
                const meta = material.generation_metadata;
                return {
                  id,
                  // カードと同じく、ナレーション本文 / 曲名 → 素材名 → 取得元の順で名前にする
                  name: ((meta?.narration_text || meta?.song_title) as string | undefined)
                    || material.name || labelMaterialSource(material),
                  scenario: material.narration_scenario?.title || null,
                  library: labelMaterialSource(material),
                  durationSeconds: material.duration_seconds,
                  previewUrl: urls.fullsize || null,
                  selected: !!selectedMap[id],
                  status: selectedMap[id] ? '選択中' : null,
                  onSelect: () => onSelectMaterial?.(material),
                };
              })}
          />
          {!paginated && activeTab === 'cloud' && cloudQuery.hasNextPage && (
            <LoadMoreButton
              onClick={() => cloudQuery.fetchNextPage()}
              isLoading={cloudQuery.isFetchingNextPage}
            />
          )}
          {!paginated && activeTab === 'temporary' && tempQuery.hasNextPage && (
            <LoadMoreButton
              onClick={() => tempQuery.fetchNextPage()}
              isLoading={tempQuery.isFetchingNextPage}
            />
          )}
        </div>
      ) : (
        <div className="materials-grid" ref={materialsGridRef} style={isMobile && (pullState.distance > 0 || pullState.refreshing) ? { paddingTop: pullState.refreshing ? 40 : pullState.distance } : undefined}>
          {isMobile && (pullState.distance > 0 || pullState.refreshing) && (
            <div className="pull-indicator" style={{ height: pullState.refreshing ? 40 : pullState.distance }}>
              {pullState.refreshing ? <span className="spinner" /> : '↓ 引っ張って更新'}
            </div>
          )}
          {displayMaterials.map(({ id, material, isDeleted, placeholder }) => {
            const urls = resolveMaterialUrls(material, gcsSignedUrls.get(id) || null);

            const isAudio = material.content_type?.startsWith('audio/');
            const imageUrl = (!isDeleted && !isAudio) ? urls.thumbnail : null;

            const fileType = normalizeFileType(material.content_type);
            const badgeLabels: (string | null)[] = [];
            let rasterStatusBadges: InfoBadge[] = [];

            // アスペクト比バッジを最初に追加
            const aspectRatio = (material as any).aspect_ratio || calculateAspectRatio(material.width, material.height);
            if (aspectRatio) {
              badgeLabels.push(aspectRatio);
            }

            if (fileType === 'video') {
              const resolutionLabel = getVideoResolutionLabel(material.width, material.height);
              if (resolutionLabel) badgeLabels.push(resolutionLabel);

              const durationLabel = formatDurationBadge(material.duration_seconds);
              if (durationLabel) badgeLabels.push(durationLabel);

              if (material.file_size_bytes && !hideFileSizeBadge) {
                const sizeMB = (material.file_size_bytes / (1024 * 1024)).toFixed(1);
                badgeLabels.push(`${sizeMB}MB`);
              }

              const meta = (material as any).generation_metadata;
              if (meta?.has_audio) {
                badgeLabels.push('音声あり');
              }
            } else if (isAudio) {
              const durationLabel = formatDurationBadge(material.duration_seconds);
              if (durationLabel) badgeLabels.push(durationLabel);

              if (material.file_size_bytes && !hideFileSizeBadge) {
                const sizeMB = (material.file_size_bytes / (1024 * 1024)).toFixed(1);
                badgeLabels.push(`${sizeMB}MB`);
              }
            } else if (isSvgContentType(material.content_type)) {
              badgeLabels.push(...createSvgBadgeLabels(material));
              // ラスタ版 (PNG) の描画に失敗した SVG は控えめに知らせる (AI 生成・案件画像には使えない)
              rasterStatusBadges = createRasterStatusBadges(material);
            } else if (fileType === 'image') {
              const sizeLabel = material.width && material.height ? `${material.width}×${material.height}` : null;
              if (sizeLabel) badgeLabels.push(sizeLabel);
            } else if (material.width && material.height) {
              badgeLabels.push(`${material.width}×${material.height}`);
            }

            // 制作進捗 (workflow stage) バッジ: 組織で有効かつ素材に設定がある場合
            if (workflowStageEnabled) {
              const stageId = (material as CloudMaterial | TemporaryMaterial).workflow_stage_id;
              if (stageId) {
                const stage = workflowStages.find((s) => s.id === stageId);
                if (stage) badgeLabels.push(stage.label);
              }
            }

            const isMarked = !isDeleted && bulkSelectedIds.has(id);
            const isSelected = !isDeleted && !!selectedMap[id];

            const handleToggleMarked = !isDeleted && bulkEditMode !== 'none'
              ? () => {
                  setBulkSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) {
                      next.delete(id);
                    } else {
                      next.add(id);
                    }
                    return next;
                  });
                }
              : undefined;

            const handleToggleActive = () => {
              if (isDeleted) return;

              if (bulkEditMode !== 'none') {
                setBulkSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) {
                    next.delete(id);
                  } else {
                    next.add(id);
                  }
                  return next;
                });
                return;
              }

              if (mode === 'select') {
                onSelectMaterial?.(material);
              } else {
                // 非ラスタ原本 (SVG) でラスタ版が未生成なら、ここで取得してから追加する
                // (features/materials/ensureRasterAsset.ts)。失敗は理由をトーストで出して中断
                const addWithRaster = (onAdded?: () => void) => {
                  setRasterPending(id, true);
                  void ensureRasterAsset(buildDragData(material, urls) as unknown as GenerationAsset)
                    .then((asset) => {
                      onAddAsset?.(asset);
                      onAdded?.();
                    })
                    .catch((error) => {
                      console.error('ラスタ版の取得に失敗:', error);
                      toast.error(rasterErrorMessage(error));
                    })
                    .finally(() => setRasterPending(id, false));
                };

                // ラスタ版の取得中は追加待ちなので、再クリックで解除しない
                if (rasterPendingIds.has(id)) return;

                // スマホの場合はワンタップで追加/削除 + トースト表示
                if (isMobile) {
                  if (selectedMap[id]) {
                    // 既に追加済みの場合は削除
                    onRemoveAsset?.(id);
                    toast.show('選択素材から外しました', 3000);
                    return;
                  }
                  // 素材を追加 (3秒間トースト表示)
                  addWithRaster(() => toast.success('選択素材に追加しました', 3000));
                  return;
                }

                // PC: 従来の挙動（トグル）
                if (selectedMap[id]) {
                  onRemoveAsset?.(id);
                } else {
                  addWithRaster();
                }
              }
            };

            const handleShowDetails = !isDeleted
              ? () => {
                  const index = navigableMaterials.findIndex((mat) => mat.id === id);
                  setActiveIndex(index >= 0 ? index : null);
                  setActiveMaterial(material);

                  // URLに material_id を追加（ブラウザ履歴に残す）
                  if (typeof window !== 'undefined') {
                    const newParams = new URLSearchParams(window.location.search);
                    newParams.set('material_id', id);
                    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
                    window.history.pushState({}, '', newUrl);
                    setUrlState({
                      pathname: window.location.pathname,
                      search: `?${newParams.toString()}`,
                    });
                  }
                }
              : undefined;

            const handleDeleteClick = mode === 'manage' && bulkEditMode === 'none' && !isDeleted
              ? async () => {
                  const tabForDelete = materialsTab;
                  setBulkSelectedIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                  });
                  addPlaceholderForMaterial(tabForDelete, material);
                  try {
                    if ('temp_path' in material) {
                      await deleteTemporaryMutation.mutateAsync(material.id);
                    } else {
                      await deleteCloudMutation.mutateAsync(material.id);
                    }
                  } catch (error) {
                    removePlaceholderForMaterial(tabForDelete, material.id);
                  }
                }
              : undefined;

            // 保護トグルハンドラー（manageモードのみ有効）
            const handleToggleProtected = mode === 'manage' && !isDeleted
              ? async () => {
                  try {
                    const newProtected = !material.protected;
                    if ('temp_path' in material) {
                      await updateTemporaryMaterial(material.id, { protected: newProtected });
                      tempQuery.refetch();
                    } else {
                      await updateCloudMaterial(material.id, { protected: newProtected });
                      cloudQuery.refetch();
                    }
                  } catch (error) {
                    console.error('Protection toggle error:', error);
                  }
                }
              : undefined;

            const { columnSpan, rowSpan, imageHeight } = getGridSize(material);

            return (
              <div
                key={id}
                className="material-item"
                style={{
                  gridColumn: `span ${columnSpan}`,
                  gridRow: `span ${rowSpan}`
                }}
              >
                <MaterialCard
                  imageUrl={!isDeleted ? imageUrl ?? undefined : undefined}
                  contentType={material.content_type}
                  width={material.width}
                  height={material.height}
                  previewAlt={normalizeAsinForDisplay(material.asin) || 'プレビュー'}
                  infoBadges={[...createInfoBadges(badgeLabels), ...rasterStatusBadges]}
                  tags={material.tags || []}
                  isActive={isSelected}
                  isMarked={isMarked}
                  checkboxChecked={isMarked}
                  isDeleted={isDeleted}
                  // 選択モード (商品画像の空き枠クリックや差替で開く) でも、商品画像枠への
                  // 直接ドロップができるようにドラッグは常に許可する (削除済みは除く)
                  draggable={!isDeleted}
                  imageHeight={imageHeight}
                  onDragStart={!isDeleted ? (e) => {
                    e.dataTransfer.setData('application/json', JSON.stringify(buildDragData(material, urls)));
                    e.dataTransfer.effectAllowed = 'copy';
                    const isVideo = material.content_type?.startsWith('video/');
                    window.dispatchEvent(new CustomEvent('material-drag-start', { detail: { isVideo } }));
                  } : undefined}
                  onToggleMarked={handleToggleMarked}
                  onToggleActive={handleToggleActive}
                  onShowDetails={hideDetailsButton ? undefined : handleShowDetails}
                  onDelete={handleDeleteClick}
                  deleteAriaLabel="ゴミ箱に移動"
                  onMeasure={!isDeleted ? (metrics) => handleMeasure(materialsTab, id, metrics) : undefined}
                  materialId={id}
                  fixedCardHeight={isDeleted ? placeholder?.cardHeight : undefined}
                  fixedPreviewHeight={isDeleted ? placeholder?.previewHeight : undefined}
                  deletedLabel="削除済み"
                  isProtected={material.protected}
                  onToggleProtected={handleToggleProtected}
                  narrationText={
                    // ナレーション本文 / 曲名が無い音声 (効果音・フリー楽曲・アップロード) も
                    // 素材名を出して再生ボタン付きのカードにする (無いと No Picture で再生できない)
                    material.content_type?.startsWith('audio/')
                      ? ((material.generation_metadata?.narration_text || material.generation_metadata?.song_title) as string | undefined)
                        || material.name || labelMaterialSource(material)
                      : undefined
                  }
                  audioUrl={
                    material.content_type?.startsWith('audio/')
                      ? (urls.fullsize || null)
                      : undefined
                  }
                  videoUrl={
                    playableVideoCards && !isDeleted && fileType === 'video'
                      ? urls.video
                      : undefined
                  }
                  selectOnClick={showSelectButton}
                />
              </div>
            );
          })}

          {/* 無限スクロール: Load More ボタン (paginated 時は出さない) */}
          {!paginated && activeTab === 'cloud' && cloudQuery.hasNextPage && (
            <div style={{ gridColumn: '1 / -1' }}>
              <LoadMoreButton
                onClick={() => cloudQuery.fetchNextPage()}
                isLoading={cloudQuery.isFetchingNextPage}
              />
            </div>
          )}

          {!paginated && activeTab === 'temporary' && tempQuery.hasNextPage && (
            <div style={{ gridColumn: '1 / -1' }}>
              <LoadMoreButton
                onClick={() => tempQuery.fetchNextPage()}
                isLoading={tempQuery.isFetchingNextPage}
              />
            </div>
          )}
        </div>
      )
      )}

      {/* ページ番号ジャンプ (paginated 時のみ・グリッド外。この位置の activeTab は cloud/temporary) */}
      {paginated && !isLoading && !error && (
        <MaterialsPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          disabled={isFetchingActivePaged}
        />
      )}

      </>)}

      {/* 詳細モーダル - クラウド素材のみ。
          深いリンクで現在ページに無い素材でも単体表示できるよう、位置の有無は条件にしない。
          左右ナビは navigateDetail 側で一覧に居ない素材のとき抑止。 */}
      {activeMaterial !== null && activeTab === 'cloud' && (
        <CloudMaterialsModal
            material={activeMaterial as CloudMaterial}
            onClose={closeDetailModal}
            onNavigate={navigateDetail}
            onDeleted={handleDetailDeleted}
            onRefresh={(updatedMaterial) => {
              if (updatedMaterial) {
                setActiveMaterial(updatedMaterial);
              }
            }}
            showAddToSelectionButton={mode === 'manage'}
        />
      )}

      {/* 詳細モーダル - 個人フォルダ素材のみ (activeIndex===null の単体表示も許可。R1) */}
      {activeMaterial !== null && activeTab === 'temporary' && (
        <MaterialPreviewModal
            material={activeMaterial as TemporaryMaterial}
            materialId={activeMaterial.id}
            isTemporary={true}
            onClose={closeDetailModal}
            onNavigate={navigateDetail}
            onDeleted={handleDetailDeleted}
            showEditControls={true}
            showAddToSelectionButton={mode === 'manage'}
            showShareButton={simpleMaterialsUi}
            onRequestRefresh={async (materialId) => {
              try {
                await queryClient.refetchQueries({ queryKey: ['temporary-materials'], exact: true });
              } catch (error) {
                console.error('一時素材の再取得に失敗しました:', error);
              }
              const latestTemporaryMaterials = queryClient.getQueryData<TemporaryMaterial[]>(['temporary-materials']) ?? temporaryMaterials;
              const updatedMaterial = latestTemporaryMaterials.find((item) => item.id === materialId) ?? null;
              if (updatedMaterial) {
                setActiveMaterial(updatedMaterial);
              }
            }}
        />
      )}

      {/* 一括操作バー + タグドロップダウン + 商品選択モーダル */}
      <MaterialsBulkActions
        bulk={bulk}
        bulkEditMode={bulkEditMode}
        onBulkEditComplete={onBulkEditComplete}
        activeTab={activeTab}
        bulkActionsTarget={bulkActionsTarget}
      />
    </div>
  );
});

export default SimpleMaterialsList;
